import { createSlice, current } from '@reduxjs/toolkit'

import {
  createAdSetThunk,
  estimateAdSetThunk,
  fetchPlatformsThunk,
  initAdSetThunk,
  publishAdSetThunk,
  saveAdSetThunk,
} from 'apps/ads/actions/adSetActionsTyped'
import * as AdSetAdapter from 'apps/ads/models/AdSet/adapter'
import {
  type AdSetItem,
  adSetInit,
  dropAdSet,
  updateAdSet,
  updateAdSetInteractionType,
} from 'apps/ads/reducers/currentAdSetActions'
import { EstimateAdSetResponse } from 'shared/api/requests/ads/schema'
import { mergeData } from 'utils/factory/mergeData'

type DevicePlatforms = ReturnType<typeof AdSetAdapter.parseDevicePlatforms>
type AdSetEstimate = EstimateAdSetResponse['estimate']

interface CurrentAdSetState {
  item: AdSetItem | null
  isInitializing: boolean
  isEstimateLoading: boolean
  isPublishing: boolean
  isCreating: boolean
  estimate: AdSetEstimate | null
  lastSaved: number | null
  lastChanged: number | null
  interactionType: string | null
  platformsData: Record<string, DevicePlatforms>
}

const initialState: CurrentAdSetState = {
  item: null,
  isInitializing: false,
  isEstimateLoading: false,
  isPublishing: false,
  isCreating: false,
  estimate: {},
  lastSaved: null,
  lastChanged: null,
  interactionType: null,
  platformsData: {},
}

const currentAdSetSlice = createSlice({
  name: 'currentAdSet',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(adSetInit, (state, action) => {
        state.item = action.payload
      })
      .addCase(updateAdSet, (state, action) => {
        state.lastChanged = Date.now()

        if (state.item == null) {
          return
        }

        const { data, path, replace } = action.payload
        state.item = mergeData(current(state).item, data, path, replace)
      })
      .addCase(dropAdSet, () => initialState)
      .addCase(updateAdSetInteractionType, (state, action) => {
        state.interactionType = action.payload
      })
      .addCase(initAdSetThunk.pending, (state) => {
        state.isInitializing = true
      })
      .addCase(initAdSetThunk.fulfilled, (state) => {
        state.isInitializing = false
      })
      .addCase(initAdSetThunk.rejected, (state) => {
        state.isInitializing = false
      })
      .addCase(createAdSetThunk.pending, (state) => {
        state.isCreating = true
      })
      .addCase(createAdSetThunk.fulfilled, (state) => {
        state.isCreating = false
      })
      .addCase(createAdSetThunk.rejected, (state) => {
        state.isCreating = false
      })
      .addCase(publishAdSetThunk.pending, (state) => {
        state.isPublishing = true
      })
      .addCase(publishAdSetThunk.fulfilled, (state) => {
        state.isPublishing = false
      })
      .addCase(publishAdSetThunk.rejected, (state) => {
        state.isPublishing = false
      })
      .addCase(estimateAdSetThunk.pending, (state) => {
        state.isEstimateLoading = true
      })
      .addCase(estimateAdSetThunk.fulfilled, (state, action) => {
        state.isEstimateLoading = false
        state.estimate = action.payload
      })
      .addCase(estimateAdSetThunk.rejected, (state) => {
        state.isEstimateLoading = false
        state.estimate = null
      })
      .addCase(fetchPlatformsThunk.fulfilled, (state, action) => {
        const { deviceType, data } = action.payload
        state.platformsData[deviceType] = AdSetAdapter.parseDevicePlatforms(deviceType, data)
      })
      .addCase(saveAdSetThunk.fulfilled, (state) => {
        state.lastSaved = Date.now()
      })
  },
})

export const currentAdSetReducer = currentAdSetSlice.reducer
